/**
 * Prefira remover `min`/`max` nativos em campos de km e validar com numberGuards:
 * setCustomValidity+reportValidity ainda pode falhar em alguns browsers/locales.
 */
/**
 * Mensagens de validação HTML5 em pt-BR (setCustomValidity),
 * já que o browser usa o idioma do SO e pode mostrar inglês.
 *
 * Importante: limpar customValidity ANTES de ler validity.*, senão
 * customError mascara rangeUnderflow/valueMissing. Aplicar a mensagem
 * também em input/change e no submit (reportValidity), não só em onInvalid —
 * o tooltip nativo EN é composto antes do onInvalid na 1ª vez.
 */
import type { FormEvent, InvalidEvent } from 'react';

type ValidityTarget = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function isValidityTarget(el: Element): el is ValidityTarget {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
  );
}

export function setPtBrValidityMessage(el: ValidityTarget): void {
  // Reset para que as flags nativas (rangeUnderflow, etc.) fiquem corretas.
  el.setCustomValidity('');
  if (el.validity.valid) return;

  if (el.validity.valueMissing) {
    el.setCustomValidity('Preencha este campo.');
    return;
  }
  if (el.validity.rangeUnderflow) {
    const min = (el as HTMLInputElement).min;
    el.setCustomValidity(
      min !== '' ? `O valor deve ser maior ou igual a ${min}.` : 'Valor abaixo do mínimo permitido.'
    );
    return;
  }
  if (el.validity.rangeOverflow) {
    const max = (el as HTMLInputElement).max;
    el.setCustomValidity(
      max !== '' ? `O valor deve ser menor ou igual a ${max}.` : 'Valor acima do máximo permitido.'
    );
    return;
  }
  if (el.validity.stepMismatch) {
    el.setCustomValidity('Informe um valor válido.');
    return;
  }
  if (el.validity.badInput) {
    el.setCustomValidity('Informe um número válido.');
    return;
  }
  if (el.validity.typeMismatch) {
    el.setCustomValidity('Formato inválido.');
    return;
  }
  if (el.validity.patternMismatch) {
    el.setCustomValidity('Formato inválido.');
    return;
  }
  if (el.validity.tooShort) {
    el.setCustomValidity('Texto muito curto.');
    return;
  }
  if (el.validity.tooLong) {
    el.setCustomValidity('Texto muito longo.');
    return;
  }
  el.setCustomValidity('Valor inválido.');
}

export function clearPtBrValidity(el: ValidityTarget): void {
  el.setCustomValidity('');
}

export function applyPtBrValidityToForm(form: HTMLFormElement): void {
  Array.from(form.elements).forEach(el => {
    if (isValidityTarget(el)) setPtBrValidityMessage(el);
  });
}

/** onSubmit: aplica pt-BR e só segue se o form for válido (reportValidity). */
export function handlePtBrSubmit(e: FormEvent<HTMLFormElement>, onValid: () => void): void {
  e.preventDefault();
  const form = e.currentTarget;
  applyPtBrValidityToForm(form);
  if (!form.checkValidity()) {
    form.reportValidity();
    const firstInvalid = form.querySelector(':invalid') as HTMLElement | null;
    firstInvalid?.focus();
    return;
  }
  onValid();
}

/** Props prontas para espalhar em inputs/selects/textareas. */
export const ptBrValidityProps = {
  lang: 'pt-BR' as const,
  onInvalid: (e: InvalidEvent<ValidityTarget>) => {
    setPtBrValidityMessage(e.currentTarget);
  },
  onInput: (e: FormEvent<ValidityTarget>) => {
    setPtBrValidityMessage(e.currentTarget);
  },
};
